import React, { useEffect, useMemo, useState } from "react";
import StudentDashboardLayout from "./StudentDashboardLayout";
import {
  getMyExamSessionsAPI,
  getMyPublishedResultsAPI,
} from "../../utils/Api";

const STATUS_CLASS_NAMES = {
  SUCCESSFUL: "bg-green-100 text-green-700",
  PASS: "bg-green-100 text-green-700",
  FAIL: "bg-red-100 text-red-700",
  UNSUCCESSFUL: "bg-red-100 text-red-700",
  ABSENT: "bg-amber-100 text-amber-700",
  PENDING: "bg-gray-100 text-gray-700",
  WITHHELD: "bg-purple-100 text-purple-700",
};

const formatDateTime = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const formatValue = (value, fallback = "-") => {
  if (value === null || value === undefined || value === "") return fallback;
  return value;
};

const formatPercentage = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "-";
  return `${Math.round(numericValue * 100) / 100}%`;
};

const normalizeLabel = (value, fallback = "-") => {
  if (!value) return fallback;
  return String(value).trim().replace(/_/g, " ").toUpperCase();
};

const getStatusClassName = (value) =>
  STATUS_CLASS_NAMES[value] || "bg-gray-100 text-gray-700";

const getMarksBreakdown = (result) => {
  if (
    Array.isArray(result?.marksBreakdown) &&
    result.marksBreakdown.length > 0
  ) {
    return result.marksBreakdown;
  }

  if (Array.isArray(result?.marks) && result.marks.length > 0) {
    return result.marks;
  }

  return [];
};

const normalizeSubjectResult = (result, index) => {
  const subjectCode = formatValue(
    result?.subjectCode || result?.code || result?.courseCode,
    "",
  );
  const subjectName = formatValue(
    result?.subjectName || result?.name || result?.courseTitle,
    `Subject ${index + 1}`,
  );
  const resultLabel = normalizeLabel(
    result?.resultLabel ||
      result?.result ||
      result?.statusLabel ||
      result?.status,
    "PENDING",
  );

  return {
    key:
      result?.subjectId ||
      `${subjectCode || "subject"}-${subjectName}-${index}`,
    rowType: result?.rowType || "subject",
    subjectCode,
    subjectName,
    internalDisplay: formatValue(
      result?.internalDisplay ?? result?.iDisplay ?? result?.internalObtained,
    ),
    internalMaximum: formatValue(
      result?.internalMaximum ?? result?.maxInternal,
    ),
    internalMinimum: formatValue(
      result?.internalMinimum ?? result?.minInternal,
    ),
    externalDisplay: formatValue(
      result?.externalDisplay ?? result?.eDisplay ?? result?.externalObtained,
    ),
    externalMaximum: formatValue(
      result?.externalMaximum ?? result?.maxExternal,
    ),
    externalMinimum: formatValue(
      result?.externalMinimum ?? result?.minExternal,
    ),
    totalDisplay: formatValue(
      result?.totalDisplay ?? result?.totalObtained ?? result?.total,
    ),
    totalMaximum: formatValue(result?.totalMaximum ?? result?.maxTotal),
    totalMinimum: formatValue(result?.totalMinimum ?? result?.minTotal),
    grade: formatValue(result?.grade),
    gradePoint: formatValue(result?.gradePoint ?? result?.gp),
    credit: formatValue(result?.credit),
    creditEarned: formatValue(result?.creditEarned ?? result?.earned),
    creditGrade: formatValue(result?.creditGrade ?? result?.cg),
    percentage: formatPercentage(result?.percentage),
    resultLabel,
    remarks: result?.remarks || "",
    breakdown: getMarksBreakdown(result),
    updatedAt: result?.updatedAt || null,
    graceMarks: Number(result?.graceMarks) || 0,
    graceAppliedTo: result?.graceAppliedTo || "",
    dlleApplied: !!result?.dlleApplied,
    dlleGraceMarks: Number(result?.dlleGraceMarks) || 0,
  };
};

const mergeResultRows = (results) =>
  (Array.isArray(results) ? results : []).map((result, index) =>
    normalizeSubjectResult(result, index),
  );

const ViewMarks = () => {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingResults, setLoadingResults] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getMyExamSessionsAPI();
        const fetchedSessions = response.sessions || [];
        setSessions(fetchedSessions);

        const firstPublishedSession = fetchedSessions.find(
          (session) => session.hasPublishedResults,
        );
        setSelectedSession(firstPublishedSession || null);
      } catch (err) {
        setError(
          err.response?.data?.message || "Failed to fetch exam sessions",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchSessions();
  }, []);

  useEffect(() => {
    const fetchResults = async () => {
      if (!selectedSession?.hasPublishedResults) {
        setResults(null);
        setLoadingResults(false);
        return;
      }

      try {
        setLoadingResults(true);
        setError(null);
        const response = await getMyPublishedResultsAPI(
          selectedSession._id,
          selectedSession.sessionType,
        );
        setResults(response);
      } catch (err) {
        setResults(null);
        if (err.response?.status !== 403) {
          setError(err.response?.data?.message || "Failed to fetch results");
        }
      } finally {
        setLoadingResults(false);
      }
    };

    fetchResults();
  }, [selectedSession]);

  const handleSessionChange = (sessionId) => {
    const session = sessions.find((item) => item._id === sessionId);
    setSelectedSession(session || null);
    setResults(null);
    setError(null);
  };

  const normalizedRows = useMemo(
    () => mergeResultRows(results?.results),
    [results],
  );

  const summary = results?.summary || {};
  const studentInfo = results?.studentInfo || {};
  const sessionInfo = results?.session || {};
  const resultConfig = results?.resultConfig || {};
  const canShowResults = !!selectedSession?.hasPublishedResults;
  const publishedAt = summary?.publishedAt || sessionInfo?.publishedAt || null;
  const overallResult = normalizeLabel(
    summary?.overallResult || summary?.result,
    normalizedRows.length > 0 ? "PUBLISHED" : "-",
  );
  const summaryItems = [
    { label: "Remark", value: overallResult },
    {
      label: "Total Marks",
      value: `${formatValue(summary?.grandTotalObtained, 0)}/${formatValue(
        summary?.grandTotalMaximum,
        0,
      )}`,
    },
    {
      label: "Percentage",
      value: formatPercentage(summary?.overallPercentage),
    },
    {
      label: "Subjects",
      value: formatValue(summary?.totalSubjects ?? normalizedRows.length, 0),
    },
    publishedAt
      ? {
          label: "Published On",
          value: formatDateTime(publishedAt),
        }
      : null,
    summary?.totalCreditsEarned != null
      ? {
          label: "Credits Earned",
          value: formatValue(summary.totalCreditsEarned),
        }
      : null,
    summary?.finalGrade
      ? {
          label: "Final Grade",
          value: formatValue(summary.finalGrade),
        }
      : null,
  ].filter(Boolean);

  return (
    <StudentDashboardLayout>
      <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
        <h1 className="text-2xl font-bold mb-5 text-gray-800">Exam Results</h1>

        <div className="mb-6">
          <label className="block text-sm font-medium mb-2 text-gray-700">
            Exam Session
          </label>
          {loading ? (
            <p className="text-gray-500 text-sm">Loading sessions...</p>
          ) : sessions.length === 0 ? (
            <p className="text-gray-500 text-sm">No exam sessions found.</p>
          ) : (
            <select
              value={selectedSession?._id || ""}
              onChange={(e) => handleSessionChange(e.target.value)}
              className="w-full md:w-96 p-2.5 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select Session</option>
              {sessions.map((session) => (
                <option key={session._id} value={session._id}>
                  {session.title} ({session.academicYear} - {session.term})
                  {session.sessionType === "atkt" ? " [ATKT]" : ""}
                  {session.hasPublishedResults ? " - Published" : ""}
                </option>
              ))}
            </select>
          )}
        </div>

        {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

        {!loading && !selectedSession && sessions.length > 0 && (
          <p className="text-gray-500 text-sm">
            Select an exam session to view published results.
          </p>
        )}

        {canShowResults && loadingResults && (
          <p className="text-gray-500 py-8 text-center">Loading results...</p>
        )}

        {canShowResults && !loadingResults && results?.hasResults && (
          <div className="space-y-6">
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-blue-600 text-white px-4 py-3 font-semibold">
                Session Overview
              </div>
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="space-y-2 text-gray-700">
                    <div>
                      <span className="font-semibold text-gray-900">
                        Session:
                      </span>{" "}
                      {sessionInfo?.title || selectedSession?.title || "-"}
                    </div>
                    <div>
                      <span className="font-semibold text-gray-900">
                        Academic Year:
                      </span>{" "}
                      {sessionInfo?.academicYear ||
                        selectedSession?.academicYear ||
                        "-"}
                    </div>
                    <div>
                      <span className="font-semibold text-gray-900">Term:</span>{" "}
                      {sessionInfo?.term || selectedSession?.term || "-"}
                    </div>
                    <div>
                      <span className="font-semibold text-gray-900">
                        Exam Type:
                      </span>{" "}
                      {sessionInfo?.examType || resultConfig?.examType || "-"}
                    </div>
                  </div>
                  <div className="space-y-2 text-gray-700">
                    <div>
                      <span className="font-semibold text-gray-900">
                        Programme:
                      </span>{" "}
                      {sessionInfo?.programme ||
                        resultConfig?.programme ||
                        studentInfo?.course ||
                        "-"}
                    </div>
                    <div>
                      <span className="font-semibold text-gray-900">
                        Semester:
                      </span>{" "}
                      {sessionInfo?.semester || resultConfig?.semester || "-"}
                    </div>
                    <div>
                      <span className="font-semibold text-gray-900">
                        Result Declared On:
                      </span>{" "}
                      {resultConfig?.resultDeclaredOn ||
                        formatDateTime(publishedAt)}
                    </div>
                    <div>
                      <span className="font-semibold text-gray-900">
                        Status:
                      </span>{" "}
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusClassName(
                          overallResult,
                        )}`}
                      >
                        {overallResult}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm border-t border-gray-200 pt-4">
                  <div>
                    <span className="font-semibold text-gray-900">Name:</span>{" "}
                    {studentInfo?.name || "-"}
                  </div>
                  <div>
                    <span className="font-semibold text-gray-900">
                      Roll No:
                    </span>{" "}
                    {studentInfo?.rollNumber || "-"}
                  </div>
                  <div>
                    <span className="font-semibold text-gray-900">PRN No:</span>{" "}
                    {studentInfo?.prn || "-"}
                  </div>
                  <div>
                    <span className="font-semibold text-gray-900">GR No:</span>{" "}
                    {studentInfo?.grNo || "-"}
                  </div>
                  <div>
                    <span className="font-semibold text-gray-900">Batch:</span>{" "}
                    {studentInfo?.batch || "-"}
                  </div>
                  <div>
                    <span className="font-semibold text-gray-900">Course:</span>{" "}
                    {studentInfo?.course || "-"}
                  </div>
                </div>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3 text-sm">
                  {summaryItems.map((item) => (
                    <div key={item.label}>
                      <div className="text-xs uppercase tracking-wide text-gray-500">
                        {item.label}
                      </div>
                      <div className="mt-1 font-semibold text-gray-900">
                        {item.value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-blue-600 text-white px-4 py-3 font-semibold">
                Published Subject Results
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-100 text-gray-700">
                      <th className="px-4 py-3 text-left font-semibold">
                        Code
                      </th>
                      <th className="px-4 py-3 text-left font-semibold">
                        Course Title
                      </th>
                      <th className="px-4 py-3 text-center font-semibold">
                        Internal
                      </th>
                      <th className="px-4 py-3 text-center font-semibold">
                        External
                      </th>
                      <th className="px-4 py-3 text-center font-semibold">
                        Total
                      </th>
                      <th className="px-4 py-3 text-center font-semibold">
                        Grade
                      </th>
                      <th className="px-4 py-3 text-center font-semibold">
                        GP
                      </th>
                      <th className="px-4 py-3 text-center font-semibold">
                        Credit
                      </th>
                      <th className="px-4 py-3 text-center font-semibold">
                        Earned
                      </th>
                      <th className="px-4 py-3 text-center font-semibold">
                        CG
                      </th>
                      <th className="px-4 py-3 text-center font-semibold">
                        Result
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {normalizedRows.map((row) => (
                      <tr
                        key={row.key}
                        className="border-t border-gray-100 hover:bg-gray-50"
                      >
                        <td className="px-4 py-3 text-gray-800 font-medium">
                          {row.subjectCode || "-"}
                        </td>
                        <td className="px-4 py-3 text-gray-800">
                          {row.subjectName}
                          {row.rowType === "practical" && (
                            <span className="ml-2 text-xs text-blue-600 font-medium">
                              Practical
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center text-gray-700">
                          <div>{row.internalDisplay}</div>
                          {row.internalMaximum !== "-" && (
                            <div className="text-xs text-gray-400">
                              / {row.internalMaximum}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center text-gray-700">
                          <div>{row.externalDisplay}</div>
                          {row.externalMaximum !== "-" && (
                            <div className="text-xs text-gray-400">
                              / {row.externalMaximum}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center font-medium text-gray-800">
                          <div>{row.totalDisplay}</div>
                          {row.totalMaximum !== "-" && (
                            <div className="text-xs text-gray-400">
                              / {row.totalMaximum}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center text-gray-700">
                          {row.grade}
                        </td>
                        <td className="px-4 py-3 text-center text-gray-700">
                          {row.gradePoint}
                        </td>
                        <td className="px-4 py-3 text-center text-gray-700">
                          {row.credit}
                        </td>
                        <td className="px-4 py-3 text-center text-gray-700">
                          {row.creditEarned}
                        </td>
                        <td className="px-4 py-3 text-center text-gray-700">
                          {row.creditGrade}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusClassName(
                              row.resultLabel,
                            )}`}
                          >
                            {row.resultLabel}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {normalizedRows.some((row) => row.breakdown.length > 0) && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-blue-600 text-white px-4 py-3 font-semibold">
                  Detailed Breakdown
                </div>
                <div className="divide-y divide-gray-200">
                  {normalizedRows
                    .filter((row) => row.breakdown.length > 0)
                    .map((row) => (
                      <div key={`${row.key}-breakdown`} className="p-4">
                        <div className="font-semibold text-gray-800 mb-3">
                          {row.subjectName}
                          {row.subjectCode ? ` (${row.subjectCode})` : ""}
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm border border-gray-200">
                            <thead>
                              <tr className="bg-gray-100 text-gray-700">
                                <th className="px-4 py-2 text-left font-semibold">
                                  Component
                                </th>
                                <th className="px-4 py-2 text-center font-semibold">
                                  Obtained
                                </th>
                                <th className="px-4 py-2 text-center font-semibold">
                                  Maximum
                                </th>
                                <th className="px-4 py-2 text-center font-semibold">
                                  Display
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {row.breakdown.map((mark, index) => (
                                <tr
                                  key={`${row.key}-mark-${index}`}
                                  className="border-t border-gray-100"
                                >
                                  <td className="px-4 py-2 text-gray-800">
                                    {mark?.schemeName ||
                                      `Component ${index + 1}`}
                                  </td>
                                  <td className="px-4 py-2 text-center text-gray-700">
                                    {formatValue(mark?.obtainedMarks, 0)}
                                  </td>
                                  <td className="px-4 py-2 text-center text-gray-700">
                                    {formatValue(mark?.maxMarks)}
                                  </td>
                                  <td className="px-4 py-2 text-center text-gray-700">
                                    {formatValue(mark?.display)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}

        {canShowResults &&
          !loadingResults &&
          results &&
          !results.hasResults && (
            <p className="text-gray-500 py-8 text-center">
              No published results are available for this session.
            </p>
          )}
      </div>
    </StudentDashboardLayout>
  );
};

export default ViewMarks;
